import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import MicrosoftCalendarSync from '../components/MicrosoftCalendarSync';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { getInitials } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, 
  Bell, 
  Clock, 
  Shield,
  Moon,
  Sun,
  Monitor,
  LogOut,
  Mail,
  Flame,
  Calendar,
  FileText,
  Save,
  Eye,
  EyeOff,
  Download,
  Link2,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Globe,
  Languages,
  Briefcase,
  FolderKanban,
  CalendarDays,
  Timer,
  Camera,
  Building2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Settings = () => {
  const { api, user, refreshUser, updateUser, logout } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: '',
    job_title: '',
    timezone: 'UTC',
    language: 'en',
    profile_photo: null
  });

  // Password state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    theme: 'system',
    default_project_id: null,
    first_day_of_week: 'monday',
    time_format: '24h',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    daily_hours_target: 8
  });

  // Notification settings (from notifications/settings API)
  // Fix 53: Daily Tracking Reminder and Idle Alert ON by default
  const [notificationSettings, setNotificationSettings] = useState({
    daily_reminder_enabled: true,  // ON by default - core app usage
    daily_reminder_time: '10:00',
    idle_alert_enabled: true,      // ON by default - core app usage
    idle_alert_threshold: 15,
    end_of_day_enabled: false,     // OFF - optional engagement
    end_of_day_time: '18:00',
    weekly_recap_enabled: false,   // OFF - optional engagement
    streak_milestones_enabled: false // OFF - optional engagement
  });

  // Reference data
  const [timezones, setTimezones] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Fix 54: Workspace settings for admin overrides
  const [workspaceSettings, setWorkspaceSettings] = useState({
    daily_hours_target_enabled: false,
    daily_hours_target_default: 8,
    daily_hours_target_max: 12
  });

  // Export state
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const photoInputRef = useState(null);

  // Calendar integration states
  const [calendarIntegrations, setCalendarIntegrations] = useState({
    google: { connected: false, connecting: false },
    outlook: { connected: false, connecting: false }
  });
  const [showIntegrationModal, setShowIntegrationModal] = useState(null); // 'google' | 'outlook' | null

  // Collapsible states
  const [integrationsOpen, setIntegrationsOpen] = useState(true);

  const WORKING_DAYS = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  useEffect(() => {
    fetchAllData();
  }, [api]);

  const fetchAllData = async () => {
    if (!api) return;
    try {
      const [profileRes, notifRes, tzRes, langRes, projectsRes, wsRes] = await Promise.all([
        api.get('/profile'),
        api.get('/notifications/settings').catch(() => ({ data: {} })),
        api.get('/profile/timezones').catch(() => ({ data: [] })),
        api.get('/profile/languages').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/workspace-settings').catch(() => ({ data: {} }))
      ]);
      
      // Fix 54: Get workspace settings for admin overrides
      if (wsRes.data) {
        setWorkspaceSettings({
          daily_hours_target_enabled: wsRes.data.daily_hours_target_enabled || false,
          daily_hours_target_default: wsRes.data.daily_hours_target_default || 8,
          daily_hours_target_max: wsRes.data.daily_hours_target_max || 12
        });
      }

      const p = profileRes.data;
      
      // Fix 52: Auto-detect timezone from browser if not set
      let detectedTimezone = p.timezone;
      if (!p.timezone || p.timezone === 'UTC') {
        try {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          // Check if browser timezone exists in our list
          if (tzRes.data.some(tz => tz.value === browserTimezone)) {
            detectedTimezone = browserTimezone;
          } else {
            // Default to IST for Indian locale or keep browser timezone
            const locale = navigator.language || navigator.userLanguage;
            if (locale && (locale.includes('IN') || locale.includes('hi'))) {
              detectedTimezone = 'Asia/Kolkata'; // IST
            } else {
              detectedTimezone = browserTimezone || 'UTC';
            }
          }
        } catch (e) {
          console.log('Could not detect browser timezone');
          detectedTimezone = 'UTC';
        }
      }
      
      setProfile({
        name: p.name || '',
        job_title: p.job_title || '',
        timezone: detectedTimezone,
        language: p.language || 'en',
        profile_photo: p.profile_photo
      });
      
      setOrganizationName(p.organization_name || '');

      setPreferences({
        theme: p.theme || 'system',
        default_project_id: p.default_project_id || null,
        first_day_of_week: p.first_day_of_week || 'monday',
        time_format: p.time_format || '24h',
        working_days: p.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        daily_hours_target: p.daily_hours_target || 8
      });

      setNotificationSettings(prev => ({ ...prev, ...notifRes.data }));
      setTimezones(tzRes.data);
      setLanguages(langRes.data);
      setProjects(projectsRes.data);

      // Apply theme
      applyTheme(p.theme || 'system');
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const applyTheme = (theme) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      // System
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/profile', {
        ...profile,
        ...preferences
      });
      toast.success('Profile saved successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', notificationSettings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await api.put('/profile/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleExport = async (format) => {
    // Validate date selection is mandatory
    if (!exportStartDate || !exportEndDate) {
      toast.error('Please select both start and end dates before exporting');
      return;
    }
    
    // Validate end date is not before start date
    if (new Date(exportEndDate) < new Date(exportStartDate)) {
      toast.error('End date cannot be before start date');
      return;
    }
    
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      params.append('start_date', exportStartDate);
      params.append('end_date', exportEndDate);

      if (format === 'csv') {
        const response = await api.get(`/profile/export?${params.toString()}`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `inspirotracker-export-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Data exported successfully');
      } else if (format === 'pdf') {
        // Fetch data for PDF
        params.set('format', 'json');
        const response = await api.get(`/profile/export?${params.toString()}`);
        const data = response.data;
        
        // Generate PDF using browser's print API
        const printWindow = window.open('', '_blank');
        const dateRange = `${exportStartDate} to ${exportEndDate}`;
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Inspiro Tracker Timesheet Export</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1e293b; }
              .header { display: flex; align-items: center; gap: 16px; margin-bottom: 32px; border-bottom: 2px solid #e2e8f0; padding-bottom: 24px; }
              .logo { width: 48px; height: 48px; background: linear-gradient(135deg, #2563eb, #4f46e5); border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; }
              .title { font-size: 24px; font-weight: 700; color: #0f172a; }
              .subtitle { font-size: 14px; color: #64748b; margin-top: 4px; }
              .meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-bottom: 32px; }
              .meta-item { background: #f8fafc; padding: 16px; border-radius: 8px; }
              .meta-label { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; }
              .meta-value { font-size: 16px; font-weight: 600; margin-top: 4px; }
              table { width: 100%; border-collapse: collapse; margin-top: 24px; }
              th { text-align: left; padding: 12px; background: #f1f5f9; font-weight: 600; font-size: 12px; text-transform: uppercase; color: #475569; border-bottom: 2px solid #e2e8f0; }
              td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
              .total-row { background: #f8fafc; font-weight: 600; }
              .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #94a3b8; }
              @media print { body { padding: 20px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="logo">✦</div>
              <div>
                <div class="title">Inspiro Tracker</div>
                <div class="subtitle">Timesheet Export</div>
              </div>
            </div>
            <div class="meta">
              <div class="meta-item">
                <div class="meta-label">Employee</div>
                <div class="meta-value">${profile.name || user?.name || 'Employee'}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Date Range</div>
                <div class="meta-value">${dateRange}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Total Entries</div>
                <div class="meta-value">${data.entries?.length || 0}</div>
              </div>
              <div class="meta-item">
                <div class="meta-label">Generated</div>
                <div class="meta-value">${new Date().toLocaleDateString()}</div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Description</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                ${(data.entries || []).map(entry => `
                  <tr>
                    <td>${entry.date || '-'}</td>
                    <td>${entry.project || 'No Project'}</td>
                    <td>${entry.description || '-'}</td>
                    <td>${entry.hours ? entry.hours.toFixed(2) + 'h' : entry.duration_formatted || '-'}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="3">Total Hours</td>
                  <td>${((data.entries || []).reduce((sum, e) => sum + (e.hours || 0), 0)).toFixed(2)}h</td>
                </tr>
              </tbody>
            </table>
            <div class="footer">
              Generated by Inspiro Tracker · ${new Date().toISOString()}
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
        toast.success('PDF generated - use Print dialog to save');
      }
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please upload a JPG or PNG image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        setProfile(prev => ({ ...prev, profile_photo: base64 }));
        
        // Save immediately
        try {
          await api.put('/profile', { profile_photo: base64 });
          toast.success('Profile photo updated');
          // Update user context immediately for header avatar sync
          if (updateUser) updateUser({ profile_photo: base64 });
          // Also refresh from server to ensure consistency
          if (refreshUser) refreshUser();
        } catch (error) {
          toast.error('Failed to save photo');
          setProfile(prev => ({ ...prev, profile_photo: null }));
        }
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  // Calendar integration handlers
  const handleConnectCalendar = (provider) => {
    setShowIntegrationModal(provider);
  };

  const handleConfirmConnect = async (provider) => {
    setShowIntegrationModal(null);
    setCalendarIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], connecting: true }
    }));

    // Simulate OAuth flow (will be replaced with real OAuth in Phase 2)
    await new Promise(resolve => setTimeout(resolve, 2000));

    setCalendarIntegrations(prev => ({
      ...prev,
      [provider]: { connected: true, connecting: false }
    }));

    const providerName = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
    toast.success(`${providerName} connected successfully`);
  };

  const handleDisconnectCalendar = async (provider) => {
    setCalendarIntegrations(prev => ({
      ...prev,
      [provider]: { ...prev[provider], connecting: true }
    }));

    // Simulate disconnect (will be replaced with real API call in Phase 2)
    await new Promise(resolve => setTimeout(resolve, 1000));

    setCalendarIntegrations(prev => ({
      ...prev,
      [provider]: { connected: false, connecting: false }
    }));

    const providerName = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
    toast.success(`${providerName} disconnected`);
  };

  const getIntegrationInfo = (provider) => {
    const info = {
      google: {
        name: 'Google Calendar',
        description: 'Sync your time entries to your personal Google Calendar',
        icon: '📅',
        color: 'text-blue-600',
        bgColor: 'bg-red-50 dark:bg-red-900/10',
        modalDescription: 'Connect your Google Calendar to automatically sync your time entries. Each time entry will appear as an event in your calendar, making it easy to see how you spent your time.'
      },
      outlook: {
        name: 'Outlook Calendar',
        description: 'Sync your time entries to your Outlook or Microsoft 365 calendar',
        icon: '📆',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50 dark:bg-blue-900/10',
        modalDescription: 'Connect your Outlook Calendar to automatically sync your time entries. Works with Outlook.com, Microsoft 365, and Exchange accounts.'
      }
    };
    return info[provider];
  };

  const handleThemeChange = async (theme) => {
    setPreferences(prev => ({ ...prev, theme }));
    localStorage.setItem('inspirotracker_theme', theme);
    applyTheme(theme);
    // Persist to backend profile immediately
    try {
      await api.put('/profile', { theme });
    } catch (e) {
      // localStorage is already set, so it'll work on next refresh anyway
    }
  };

  const passwordStrength = useMemo(() => {
    const pwd = passwordForm.new_password;
    if (!pwd) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (pwd.length >= 8) score += 25;
    if (pwd.length >= 12) score += 15;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score += 20;
    if (/\d/.test(pwd)) score += 20;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 20;

    if (score < 40) return { score, label: 'Weak', color: 'bg-red-50 dark:bg-red-900/30' };
    if (score < 70) return { score, label: 'Fair', color: 'bg-amber-50 dark:bg-amber-900/30' };
    if (score < 90) return { score, label: 'Good', color: 'bg-blue-50 dark:bg-blue-900/30' };
    return { score, label: 'Strong', color: 'bg-emerald-50 dark:bg-emerald-900/30' };
  }, [passwordForm.new_password]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('nav.settings')}
            </h1>
            <p className="text-slate-500 mt-1">{t('settings.subtitle')}</p>
          </div>
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            className="btn-premium h-10"
            data-testid="save-all-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('timeTracker.saving') : t('settings.saveAll')}
          </Button>
        </div>

        {/* Profile Section */}
        <div className="premium-card" data-testid="profile-section">
          <div className="p-6">
            <div className="section-header mb-6">
              <User className="w-4 h-4" />
              <span>{t('settings.profileSection')}</span>
            </div>
            
            {/* Avatar with Upload */}
            <div className="flex items-center gap-6 mb-6">
              <div className="relative group">
                <Avatar className="h-20 w-20 ring-4 ring-white dark:ring-slate-800 shadow-xl">
                  {profile.profile_photo ? (
                    <AvatarImage src={profile.profile_photo} alt={profile.name} />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-2xl font-semibold">
                      {getInitials(profile.name)}
                    </AvatarFallback>
                  )}
                </Avatar>
                {/* Upload overlay */}
                <label 
                  className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  htmlFor="photo-upload"
                >
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                {/* Small camera badge */}
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800">
                  <Camera className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  {profile.name || 'Your Name'}
                </h3>
                <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {t('settings.uploadPhoto')}
                </p>
              </div>
            </div>
            
            <Separator className="my-6" />
            
            {/* Profile Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="label-premium">{t('settings.fullName')}</Label>
                <Input 
                  value={profile.name} 
                  onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                  className="input-premium"
                  placeholder="John Doe"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-premium">Job Title</Label>
                <Input 
                  value={profile.job_title} 
                  onChange={(e) => setProfile(prev => ({ ...prev, job_title: e.target.value }))}
                  className="input-premium"
                  placeholder="Software Developer"
                  data-testid="profile-job-title-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="label-premium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  Company
                </Label>
                <Input 
                  value={organizationName} 
                  disabled
                  className="input-premium bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 cursor-not-allowed"
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-premium flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-400" />
                  Timezone
                </Label>
                <Select 
                  value={profile.timezone} 
                  onValueChange={(value) => setProfile(prev => ({ ...prev, timezone: value }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-60">
                    {timezones.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Change Password */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-400" />
                  {t('settings.password')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">{t('settings.changePasswordDesc')}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordDialog(true)}
                className="h-10 rounded-xl"
                data-testid="change-password-btn"
              >
                {t('settings.changePassword')}
              </Button>
            </div>
          </div>
        </div>

        {/* Appearance & Preferences */}
        <div className="premium-card" data-testid="preferences-section">
          <div className="p-6">
            <div className="section-header mb-6">
              <Monitor className="w-4 h-4" />
              <span>{t('settings.appearance')}</span>
            </div>

            {/* Theme */}
            <div className="flex items-center justify-between py-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-white">{t('settings.theme')}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Choose your preferred color scheme</p>
              </div>
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
                {[
                  { value: 'light', icon: Sun, label: t('settings.lightMode') },
                  { value: 'dark', icon: Moon, label: t('settings.darkMode') },
                  { value: 'system', icon: Monitor, label: t('settings.systemDefault') }
                ].map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    onClick={() => handleThemeChange(value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      preferences.theme === value 
                        ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`theme-${value}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Default Project */}
            <div className="flex items-center justify-between py-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                  <FolderKanban className="w-4 h-4 text-slate-400" />
                  Default Project
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Auto-select when starting timer</p>
              </div>
              <Select 
                value={preferences.default_project_id || 'none'} 
                onValueChange={(value) => setPreferences(prev => ({ 
                  ...prev, 
                  default_project_id: value === 'none' ? null : value 
                }))}
              >
                <SelectTrigger className="w-48 h-10 rounded-xl" data-testid="default-project-select">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: project.color }}
                        />
                        {project.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Format */}
            <div className="flex items-center justify-between py-4">
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  Time Format
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">How times are displayed</p>
              </div>
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
                {[
                  { value: '12h', label: '12h AM/PM' },
                  { value: '24h', label: '24h' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setPreferences(prev => ({ ...prev, time_format: value }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      preferences.time_format === value 
                        ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`time-format-${value}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Working Hours - Removed */}

        {/* Notifications - Removed */}

        {/* Data Export (GDPR) */}
        <div className="premium-card" data-testid="export-section">
          <div className="p-6">
            <div className="section-header mb-6">
              <Download className="w-4 h-4" />
              <span>Export Your Data</span>
            </div>

            <p className="text-sm text-slate-500 mb-4">
              Download all your time tracking data. This export includes only your personal entries.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Start Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className={`h-10 rounded-xl ${!exportStartDate ? 'border-slate-300' : 'border-emerald-300'}`}
                  data-testid="export-start-date"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">End Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  className={`h-10 rounded-xl ${!exportEndDate ? 'border-slate-300' : 'border-emerald-300'}`}
                  data-testid="export-end-date"
                  required
                />
              </div>
            </div>

            {(!exportStartDate || !exportEndDate) && (
              <p className="text-xs text-amber-600 mb-4">Please select date range to export your data</p>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => handleExport('csv')}
                disabled={exporting || !exportStartDate || !exportEndDate}
                className="h-10 rounded-xl"
                data-testid="export-csv-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('pdf')}
                disabled={exporting || !exportStartDate || !exportEndDate}
                className="h-10 rounded-xl"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Microsoft Calendar Sync */}
        <MicrosoftCalendarSync />

        {/* Connected Integrations */}
        <Collapsible open={integrationsOpen} onOpenChange={setIntegrationsOpen}>
          <div className="premium-card" data-testid="integrations-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="section-header mb-0">
                <Link2 className="w-4 h-4" />
                <span>Connected Integrations</span>
              </div>
              {integrationsOpen ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-3">
                {/* Google Calendar */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Google Calendar
                      </h4>
                      <p className="text-xs text-slate-500">
                        Sync your time entries to your personal Google Calendar
                      </p>
                    </div>
                  </div>
                  {calendarIntegrations.google.connecting ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="h-9 rounded-xl min-w-[100px]"
                    >
                      <div className="w-4 h-4 mr-2 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
                      {calendarIntegrations.google.connected ? 'Disconnecting...' : 'Connecting...'}
                    </Button>
                  ) : calendarIntegrations.google.connected ? (
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                        <Check className="w-3 h-3" />
                        Connected
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnectCalendar('google')}
                        className="h-8 text-xs text-slate-500 hover:text-red-600"
                        data-testid="google-calendar-disconnect"
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConnectCalendar('google')}
                      className="h-9 rounded-xl"
                      data-testid="google-calendar-connect"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Connect
                    </Button>
                  )}
                </div>

                {/* Outlook Calendar removed - use Microsoft Calendar Sync instead */}

                <p className="text-xs text-slate-400 mt-4 text-center">
                  More integrations coming soon
                </p>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Sign Out */}
        <div className="premium-card" data-testid="signout-section">
          <div className="p-6">
            <Button 
              variant="destructive" 
              onClick={logout}
              className="h-10 gap-2 rounded-xl font-semibold"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Spacer for bottom */}
        <div className="h-8" />
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label className="label-premium">Current Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                  className="pr-10 input-premium"
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label className="label-premium">New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                  className="pr-10 input-premium"
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password Strength */}
              {passwordForm.new_password && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Password strength</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score < 40 ? 'text-red-500' :
                      passwordStrength.score < 70 ? 'text-amber-500' :
                      passwordStrength.score < 90 ? 'text-blue-500' : 'text-emerald-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <Progress value={passwordStrength.score} className="h-1.5" />
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label className="label-premium">Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="pr-10 input-premium"
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <X className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
              {passwordForm.confirm_password && passwordForm.new_password === passwordForm.confirm_password && (
                <p className="text-xs text-emerald-500 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>

            <Button 
              onClick={handleChangePassword}
              className="w-full btn-premium h-11"
              disabled={
                !passwordForm.current_password || 
                !passwordForm.new_password || 
                passwordForm.new_password !== passwordForm.confirm_password ||
                passwordForm.new_password.length < 8
              }
              data-testid="save-password-btn"
            >
              Change Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Integration Modal */}
      <Dialog open={!!showIntegrationModal} onOpenChange={() => setShowIntegrationModal(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="integration-modal">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              {showIntegrationModal === 'google' && (
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
              )}
              {showIntegrationModal === 'outlook' && (
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576a.805.805 0 01-.578.234h-8.378v-6.87l1.195.899a.409.409 0 00.503 0l7.257-5.477a.253.253 0 01.239.16zm-.816-1.376l-8.37 6.312-2.099-1.58V3.75l.5-.5h9.185c.23 0 .42.08.578.234a.794.794 0 01.238.577l-.032 1.95zM14.312 12.29l-.12.09-1.476 1.113v6.756h-9.61a.785.785 0 01-.578-.234.77.77 0 01-.24-.576V4.31c0-.222.08-.417.24-.576a.79.79 0 01.578-.234h8.706v7.723l2.5 1.066z"/>
                  </svg>
                </div>
              )}
              Connect {showIntegrationModal === 'google' ? 'Google Calendar' : 'Outlook Calendar'}
            </DialogTitle>
            <DialogDescription>
              {showIntegrationModal === 'google' 
                ? 'Connect your Google Calendar to automatically sync your time entries. Each time entry will appear as an event in your calendar.'
                : 'Connect your Outlook Calendar to automatically sync your time entries. Works with Outlook.com, Microsoft 365, and Exchange accounts.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">What happens when you connect:</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Time entries sync as calendar events</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Events include project and task details</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>You can disconnect at any time</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowIntegrationModal(null)}
                className="flex-1 h-11 rounded-xl"
                data-testid="integration-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleConfirmConnect(showIntegrationModal)}
                className="flex-1 h-11 rounded-xl btn-premium"
                data-testid="integration-connect-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                Connect
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Settings;
